/* global ActionCable */
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CALL_STATES from "../utils/states";
import { useDialerServiceUrl } from "../../reducers/layoutReducer/selectors";

const useDialer = ({
  actions: {
    checkValidLeads,
    createDialerSession,
    endCall,
    getPersonAndNumbers,
    logCall,
    startRecording,
    stopRecording,
    // non-async
    actionCableUpdateReceived,
    setCallState,
    setPhoneNumber,
    clearSession,
    setCallOutcome,
    setComment,
    setCallParams,
  },
  selectors: {
    callOutcome,
    callState,
    conference,
    leadUuids,
    loading,
    person,
    phoneNumbers,
    phoneNumber,
    session,
    validLeads,
  },
}) => {
  const dispatch = useDispatch();
  const dialerServiceUrl = useDialerServiceUrl();

  const [disconnect, setDisconnect] = useState(false);
  const disconnectDialer = () => setDisconnect(true);

  useEffect(() => {
    if (!session) return () => {};

    const consumer = ActionCable.createConsumer(`${dialerServiceUrl}/cable?token=${session.token}`);
    consumer.subscriptions.create(
      { channel: "ConferenceChannel", conference_uuid: session.conferenceUuid },
      {
        received(data) {
          dispatch(actionCableUpdateReceived(data));
        },
        connected() {
          this.perform("follow");
        },
      },
    );

    const cleanUp = () => {
      dispatch(clearSession());
      consumer.disconnect();
      setDisconnect(false);
    };

    if (disconnect) {
      cleanUp();
    }

    return () => cleanUp();
  }, [session, disconnect]);

  useEffect(() => {
    if (callState === CALL_STATES.Init) {
      dispatch(setCallOutcome(null));
      dispatch(setComment(""));
    }
    if (callState === CALL_STATES.Started && !session) {
      dispatch(createDialerSession());
    }
    if (session) {
      if (callState === CALL_STATES.Ended) dispatch(endCall());
    }
  }, [callState, session]);

  useEffect(() => {
    if (!Array.isArray(leadUuids) || !leadUuids.length) return;
    if (leadUuids.length === 1) dispatch(getPersonAndNumbers(leadUuids[0]));
    else dispatch(checkValidLeads(leadUuids));
  }, [leadUuids]);

  return {
    data: {
      callOutcome,
      callState,
      conference,
      leadUuids,
      person,
      phoneNumbers,
      phoneNumber,
      validLeads,
    },
    loading,
    disconnectDialer,
    actions: {
      endCall: () => dispatch(setCallState(CALL_STATES.Ended)),
      logCall: (opts) => dispatch(logCall(opts)),
      setCallOutcome: (o) => dispatch(setCallOutcome(o)),
      setCallParams: (l) => dispatch(setCallParams(l)),
      setComment: (n) => dispatch(setComment(n)),
      setPhoneNumber: (n) => dispatch(setPhoneNumber(n)),
      startCall: () => dispatch(setCallState(CALL_STATES.Started)),
      startRecording: ({ recordingConsent, isDismissed }) =>
        dispatch(startRecording({ recordingConsent, isDismissed })),
      stopRecording: () => dispatch(stopRecording()),
    },
  };
};

export default useDialer;
